import { PurchaseType } from 'api-hooks/general/model';
import { ActivityPurchaseModel } from 'api-hooks/order/activity';
import { usePurchaseReceiveAgentCommission } from 'api-hooks/order/common/mutation';
import { CruisePurchaseModel } from 'api-hooks/order/cruise';
import { HotelPurchaseModel } from 'api-hooks/order/hotel';
import { FlightPurchaseModel } from 'api-hooks/order/ticket-order';
import { DocumentPurchaseModel } from 'api-hooks/order/travel-document';
import { PaymentMethodEnum } from 'api-hooks/payment-channel';
import { ProductPurchaseModel } from 'api-hooks/product/order';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import { Button } from 'components/elements/buttons';
import { useFormState } from 'components/elements/form';
import useDialog, { DialogProvider } from 'hooks/use-dialog';
import useDismiss from 'hooks/use-dismiss';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { ReceiveCommisionDialog } from './receive-commision-dialog';

export type ReceiveCommissionDataType =
  | ActivityPurchaseModel
  | FlightPurchaseModel
  | DocumentPurchaseModel
  | CruisePurchaseModel
  | HotelPurchaseModel
  | ProductPurchaseModel;

export const ReceiveCommisionFormSchema = () =>
  Yup.object({
    acknowledgedAt: Yup.date().required().default(new Date()),
    accountableType: Yup.string().required().default(PaymentMethodEnum.Bank),
    accountableId: Yup.string().nullable().required().default(''),
    amount: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required()
      .default(0),
    description: Yup.string().nullable().default(''),
  });

export type ReceiveCommisionFormType = Yup.InferType<
  ReturnType<typeof ReceiveCommisionFormSchema>
>;

export type ReceiveCommisionMethodType = ReturnType<
  typeof useForm<ReceiveCommisionFormType>
>;

export interface ReceiveCommisionDialogProps {
  data: ReceiveCommissionDataType;
  onSubmit: (
    input: any,
    form: ReceiveCommisionMethodType,
  ) => Promise<any | undefined>;
  onClose: () => void;
}

export type ReceiveCommisionComponentProps = {
  data: ReceiveCommissionDataType;
  type: PurchaseType;
};

export default function ReceiveCommisionComponent(
  props: ReceiveCommisionComponentProps,
) {
  const { data } = props;
  const dialog = useDialog();
  const { t } = useTranslation();

  const { mutateAsync } = usePurchaseReceiveAgentCommission();
  const onDismiss = useDismiss(props.type, data.id);
  const onSubmit = React.useCallback(
    (onClose: () => void) =>
      async (
        values: ReceiveCommisionFormType,
        form: ReceiveCommisionMethodType,
      ) => {
        try {
          const result = await mutateAsync({
            body: values,
            id: data.id,
          });
          notification.success({ message: result.message });
          onDismiss();
          onClose();
        } catch (e) {
          if (e.errors) {
            formSetErrors(e.errors, form.setError);
          }
          notification.error({ message: e.message });
        }
      },
    [data.id, mutateAsync, onDismiss],
  );

  const onClick = React.useCallback(() => {
    dialog.showCustom({
      render: (onClose) => {
        return (
          //dialog provider use for prevert trigger submit to other form
          <DialogProvider>
            <ReceiveCommisionDialog
              data={data}
              onClose={onClose}
              onSubmit={onSubmit(onClose)}
            />
          </DialogProvider>
        );
      },
    });
  }, [data, dialog, onSubmit]);

  const { editable } = useFormState();

  if (editable) return null;

  return (
    <Button
      buttonType="secondary"
      variant="default"
      style={{
        minWidth: '220px',
      }}
      onClick={onClick}
    >
      {t('common:receive_commission')}
    </Button>
  );
}
